import {
  type PublicAvailabilitySlot,
  type PublicManageAvailabilityProvider,
  SCHEDULING_LIMITS,
} from "@emr/contracts";
import { localDateOf } from "../lib/appointmentTime";

/**
 * The date and time picker's plain helpers: local dates (YYYY-MM-DD on the clinic's calendar),
 * months (YYYY-MM), and the availability searches that fill a month.
 *
 * One search may span 63 days (refineAvailabilityDateRange: toDate at most 62 days after
 * fromDate), and every public request counts against the visitor's budget (30 in ten minutes,
 * public_booking_rate_limit). So the calendar asks in fixed 63-day searches counted from an
 * anchor (the first day it shows): a 60-day booking window is one request, and moving between
 * months reuses the cached searches instead of asking again.
 */

/** Days one availability search may cover. */
export const AVAILABILITY_SPAN_DAYS = 63;
/**
 * The most rows one search reads: the store stops at 10000 (one row per physician and time,
 * private.scheduling_slots) and the schema at 10000 times.
 */
export const AVAILABILITY_SLOT_LIMIT = 10_000;
/**
 * The most rows one time can stand for where an answer hides rows: a clinic-assigned answer
 * keeps one time per start for all of a clinic's physicians (at most SCHEDULING_LIMITS.providers).
 */
const ROWS_PER_HIDDEN_TIME = SCHEDULING_LIMITS.providers;

export interface DateRange {
  readonly fromDate: string;
  readonly toDate: string;
}

/** What a search answers: the clinic's time zone, the times, and on a manage link who they name. */
export interface AvailabilityAnswer {
  readonly timeZone: string;
  readonly slots: readonly PublicAvailabilitySlot[];
  readonly providers?: readonly PublicManageAvailabilityProvider[];
}

const dayMs = 86_400_000;
const utcMidnight = (date: string) => Date.parse(`${date}T00:00:00Z`);
const fromUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function addDays(date: string, days: number): string {
  return fromUtc(utcMidnight(date) + days * dayMs);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((utcMidnight(to) - utcMidnight(from)) / dayMs);
}

export function laterDate(left: string, right: string): string {
  return left > right ? left : right;
}

export function earlierDate(left: string, right: string): string {
  return left < right ? left : right;
}

export function monthOf(date: string): string {
  return date.slice(0, 7);
}

export function addMonths(month: string, count: number): string {
  const [year = 0, monthNumber = 1] = month.split("-").map(Number);
  const index = year * 12 + (monthNumber - 1) + count;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

export function firstOfMonth(month: string): string {
  return `${month}-01`;
}

export function lastOfMonth(month: string): string {
  return addDays(firstOfMonth(addMonths(month, 1)), -1);
}

/** 0 for Sunday to 6 for Saturday. */
export function weekdayOf(date: string): number {
  return new Date(utcMidnight(date)).getUTCDay();
}

/** A calendar cell: a day of the month, or an empty pad (date null) before the 1st or after the last. */
export interface CalendarCell {
  readonly key: string;
  readonly date: string | null;
}

/** The month's weeks, Sunday first, padded to whole weeks. */
export function monthWeeks(month: string): CalendarCell[][] {
  const first = firstOfMonth(month);
  const length = daysBetween(first, lastOfMonth(month)) + 1;
  const cells: CalendarCell[] = [
    ...Array.from({ length: weekdayOf(first) }, (_, index) => ({
      key: `before-${index}`,
      date: null,
    })),
    ...Array.from({ length }, (_, index) => {
      const date = addDays(first, index);
      return { key: date, date };
    }),
  ];
  while (cells.length % 7 !== 0) cells.push({ key: `after-${cells.length}`, date: null });
  return Array.from({ length: cells.length / 7 }, (_, week) => cells.slice(week * 7, week * 7 + 7));
}

/**
 * The searches that cover the part of `month` between firstDay and lastDay: each a fixed
 * 63-day span counted from `anchor`, cut to that window. Usually one; two when the month
 * crosses from one span into the next; none when the month is outside the window.
 */
export function searchesFor(
  month: string,
  anchor: string,
  firstDay: string,
  lastDay: string,
): DateRange[] {
  return searchesBetween(firstOfMonth(month), lastOfMonth(month), anchor, firstDay, lastDay);
}

/** The same fixed searches for any stretch of days: one day's is the search that holds it. */
export function searchesBetween(
  fromDay: string,
  toDay: string,
  anchor: string,
  firstDay: string,
  lastDay: string,
): DateRange[] {
  const from = laterDate(fromDay, firstDay);
  const to = earlierDate(toDay, lastDay);
  if (from > to) return [];
  const firstSpan = Math.floor(daysBetween(anchor, from) / AVAILABILITY_SPAN_DAYS);
  const lastSpan = Math.floor(daysBetween(anchor, to) / AVAILABILITY_SPAN_DAYS);
  const searches: DateRange[] = [];
  for (let span = firstSpan; span <= lastSpan; span += 1) {
    const start = addDays(anchor, span * AVAILABILITY_SPAN_DAYS);
    searches.push({
      fromDate: laterDate(start, firstDay),
      toDate: earlierDate(addDays(start, AVAILABILITY_SPAN_DAYS - 1), lastDay),
    });
  }
  return searches;
}

/** A search's answer, and the last date it speaks for in full. */
export interface LoadedSearch extends AvailabilityAnswer {
  /**
   * The search's toDate, or the day before the date where the store's cap stopped the last
   * follow-up: the days after it are not known to have no times.
   */
  readonly throughDate: string;
}

/** Whether every time in an answer is one of the store's rows (the booking page's named times). */
export function namesEveryTime(answer: AvailabilityAnswer): boolean {
  return answer.slots.every((slot) => slot.providerId !== undefined);
}

/**
 * Whether an answer may have stopped at the store's cap before `toDate`. Where each time is one
 * row (`exact`), only a full answer may. Elsewhere a time can stand for more rows: a
 * clinic-assigned answer keeps one time per start for up to 100 physicians, and a manage link's
 * answer also leaves out the visit's own time and those inside the change cutoff. So an answer
 * that ends before `toDate` with times enough for the cap at 100 rows each may have stopped too.
 */
function mayBeCutShort(
  answer: AvailabilityAnswer,
  reached: string,
  toDate: string,
  exact: boolean,
): boolean {
  if (answer.slots.length >= AVAILABILITY_SLOT_LIMIT) return true;
  return (
    !exact &&
    reached < toDate &&
    answer.slots.length * ROWS_PER_HIDDEN_TIME >= AVAILABILITY_SLOT_LIMIT
  );
}

/**
 * Runs one search. An answer that may have stopped at the store's cap was cut in the middle of
 * its last date, so the times of that date are dropped and the rest of the span is asked again
 * from it, at most `followUps` more times. Only a busy clinic needs a second request. If the
 * follow-ups run out, the answer speaks only for the days before its last date (throughDate).
 */
export async function loadSearch(
  load: (range: DateRange, signal: AbortSignal) => Promise<AvailabilityAnswer>,
  range: DateRange,
  signal: AbortSignal,
  {
    exact = () => false,
    followUps = 2,
  }: {
    /** Whether each of an answer's times is one store row; unsure (false) by default. */
    readonly exact?: (answer: AvailabilityAnswer) => boolean;
    readonly followUps?: number;
  } = {},
): Promise<LoadedSearch> {
  let answer = await load(range, signal);
  const timeZone = answer.timeZone;
  const slots: PublicAvailabilitySlot[] = [];
  const providers = new Map<string, PublicManageAvailabilityProvider>();
  let named = false;
  let from = range.fromDate;
  const loaded = (throughDate: string): LoadedSearch => ({
    timeZone,
    slots,
    ...(named ? { providers: [...providers.values()] } : {}),
    throughDate,
  });
  for (let round = 0; ; round++) {
    if (answer.providers !== undefined) named = true;
    for (const provider of answer.providers ?? []) providers.set(provider.id, provider);
    const reached = answer.slots
      .map((slot) => localDateOf(slot.startAt, timeZone))
      .reduce(laterDate, from);
    // One date alone filling the cap, or an answer past the end, leaves nothing more to ask.
    const cut =
      reached > from &&
      reached <= range.toDate &&
      mayBeCutShort(answer, reached, range.toDate, exact(answer));
    if (!cut) {
      slots.push(...answer.slots);
      return loaded(range.toDate);
    }
    slots.push(...answer.slots.filter((slot) => localDateOf(slot.startAt, timeZone) < reached));
    if (round >= followUps) return loaded(addDays(reached, -1));
    from = reached;
    answer = await load({ fromDate: reached, toDate: range.toDate }, signal);
  }
}

/** The times by their local date on the clinic's clock. */
export function slotsByDay(
  slots: readonly PublicAvailabilitySlot[],
  timeZone: string,
): Map<string, PublicAvailabilitySlot[]> {
  const days = new Map<string, PublicAvailabilitySlot[]>();
  for (const slot of slots) {
    const date = localDateOf(slot.startAt, timeZone);
    const day = days.get(date);
    if (day === undefined) days.set(date, [slot]);
    else day.push(slot);
  }
  return days;
}

export type DayPeriod = "morning" | "afternoon" | "evening";

export const dayPeriodLabels: Record<DayPeriod, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
};

const hourFormats = new Map<string, Intl.DateTimeFormat>();

/** Morning before noon, afternoon before 5 p.m., then evening, on the clinic's clock. */
export function dayPeriodOf(startAt: string, timeZone: string | undefined): DayPeriod {
  const key = timeZone ?? "";
  let format = hourFormats.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-CA", {
      hour: "numeric",
      hourCycle: "h23",
      ...(timeZone === undefined ? {} : { timeZone }),
    });
    hourFormats.set(key, format);
  }
  const hour = Number(
    format.formatToParts(new Date(startAt)).find((part) => part.type === "hour")?.value,
  );
  if (hour < 12) return "morning";
  return hour < 17 ? "afternoon" : "evening";
}

/** "Toronto" for America/Toronto: how the page names the clinic's clock. */
export function timeZoneCity(timeZone: string): string {
  return (timeZone.split("/").at(-1) ?? timeZone).replaceAll("_", " ");
}

/** "Tuesday, October 13, 2026" for a local date, whatever the device's time zone. */
export function formatCalendarDate(date: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));
}

/** "October 2026". */
export function formatMonth(month: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${month}-15T12:00:00Z`));
}
